/**
 * Portal-aware label utilities
 *
 * Labels change based on whether the user is viewing as a Supplier or Customer.
 * This matches the old portal behavior where dashboard labels were context-sensitive.
 *
 * Supplier Portal labels focus on sales/revenue from customers:
 *   - "Open Sales Order Value" - value of orders from customers
 *   - "Total Revenue" - money received from customers
 *   - "Customers" - companies you supply
 *
 * Customer Portal labels focus on purchasing/spend from suppliers:
 *   - "Open Purchase Order Value" - value of orders to suppliers
 *   - "Total Spend" - money spent purchasing goods
 *   - "Suppliers" - companies you buy from
 */

type PortalType = 'SUPPLIER' | 'CUSTOMER' | 'HELIX' | 'supplier' | 'customer' | 'helix' | 'supplier_manager';

interface PortalLabels {
  // Dashboard KPI labels
  openOrderValue: string;
  totalSpend: string;
  historicalArrearsValue: string;

  // Entity labels
  customerSingular: string;
  customerPlural: string;
  supplierSingular: string;
  supplierPlural: string;

  // Section headers
  customersRequiringAttention: string;

  // Chart titles
  spendByMonth: string;

  // Ranking labels
  worstQuality: string;
  worstOTIF: string;
  worstOTD: string;
  highestArrears: string;

  // Navigation labels
  ordersMenuLabel: string;
  ordersPageTitle: string;
}

const SUPPLIER_LABELS: PortalLabels = {
  // Dashboard KPIs
  openOrderValue: 'OPEN ORDER VALUE',
  totalSpend: 'TOTAL SPEND',
  historicalArrearsValue: 'HISTORICAL ARREARS VALUE',

  // Entities
  customerSingular: 'Customer',
  customerPlural: 'Customers',
  supplierSingular: 'Supplier',
  supplierPlural: 'Suppliers',

  // Sections
  customersRequiringAttention: 'Customers Requiring Attention',

  // Charts
  spendByMonth: 'Spend by Month',

  // Rankings
  worstQuality: 'Worst Quality (PPM)',
  worstOTIF: 'Worst OTIF Performance',
  worstOTD: 'Worst On-Time Delivery',
  highestArrears: 'Highest Arrears',

  // Navigation
  ordersMenuLabel: 'Sales Orders',
  ordersPageTitle: 'Sales Orders',
};

const CUSTOMER_LABELS: PortalLabels = {
  // Dashboard KPIs - renamed to sales/revenue focus
  openOrderValue: 'OPEN PURCHASE ORDER VALUE',
  totalSpend: 'TOTAL REVENUE',
  historicalArrearsValue: 'HISTORICAL BACKLOG VALUE',

  // Entities - swapped perspective
  customerSingular: 'Customer',
  customerPlural: 'Customers',
  supplierSingular: 'Supplier',
  supplierPlural: 'Suppliers',

  // Sections - from customer's perspective, focusing on supplier relationships
  customersRequiringAttention: 'Suppliers Requiring Attention',

  // Charts
  spendByMonth: 'Revenue by Month',

  // Rankings - from customer's perspective
  worstQuality: 'Quality Issues by Supplier',
  worstOTIF: 'OTIF Issues by Supplier',
  worstOTD: 'OTD Issues by Supplier',
  highestArrears: 'Highest Backlog by Supplier',

  // Navigation
  ordersMenuLabel: 'Purchase Orders',
  ordersPageTitle: 'Purchase Orders',
};

/** Supplier-portal deployment uses the same Supplier/Customer vocabulary (no label flip). */
const SUPPLIER_PORTAL_LABELS: PortalLabels = {
  ...SUPPLIER_LABELS,
  totalSpend: 'TOTAL REVENUE',
  historicalArrearsValue: 'HISTORICAL BACKLOG VALUE',
  spendByMonth: 'Revenue by Month',
  ordersMenuLabel: 'Supplier Orders',
  ordersPageTitle: 'Supplier Orders',
};

/**
 * Get portal-specific labels based on user's platform
 */
export function getPortalLabels(platform: PortalType | undefined): PortalLabels {
  const normalized = platform?.toUpperCase();
  if (normalized === 'CUSTOMER') {
    return CUSTOMER_LABELS;
  }
  if (platform === 'supplier_manager') {
    return SUPPLIER_PORTAL_LABELS;
  }
  // Default to supplier labels (for SUPPLIER, HELIX, and undefined)
  return SUPPLIER_LABELS;
}

/**
 * Get a specific label based on portal type
 */
export function getPortalLabel(
  platform: PortalType | undefined,
  key: keyof PortalLabels
): string {
  const labels = getPortalLabels(platform);
  return labels[key];
}

/**
 * React hook for getting portal-aware labels
 * To be used with useAuth() context
 */
export function usePortalLabels(platform: PortalType | undefined) {
  return getPortalLabels(platform);
}

/**
 * Check if current view is customer portal
 */
export function isCustomerPortal(platform: PortalType | undefined): boolean {
  return platform?.toUpperCase() === 'CUSTOMER';
}

/**
 * Check if current view is supplier portal
 */
export function isSupplierPortal(platform: PortalType | undefined): boolean {
  const normalized = platform?.toUpperCase();
  return normalized === 'SUPPLIER' || normalized === 'HELIX' || platform === undefined;
}

/**
 * Column/filter label for the trading partner on the other side of a PO.
 * Supplier-side users see Customers; customer-side users see Suppliers.
 */
export function getCounterpartyLabel(isSupplierView: boolean): string {
  return isSupplierView ? 'Customer' : 'Supplier';
}

/** Placeholder for counterparty filter dropdowns. */
export function getCounterpartyFilterPlaceholder(isSupplierView: boolean): string {
  return isSupplierView ? 'All customers' : 'All suppliers';
}

/** Delivery-line / ASN supplier column: Poeton sites vs external suppliers. */
export function getDeliveryLineSupplierColumnLabel(isSupplierPortalMode: boolean): string {
  return isSupplierPortalMode ? 'Site' : 'Supplier';
}

/** Delivery-line customer column and group labels (always customer-side FK). */
export function getDeliveryLineCustomerColumnLabel(): string {
  return 'Customer';
}

export function getDeliveryLineCustomerGroupLabel(): string {
  return 'Customer Group';
}

/** Dashboard / filter labels for the customer-company FK (supplier-side view). */
export function getCustomerGroupLabel(): string {
  return 'Customer Group';
}

export function getCustomerSiteLabel(): string {
  return 'Customer Site';
}

export function getCustomerCodeLabel(): string {
  return 'Customer Code';
}

export function getCustomerTypeLabel(): string {
  return 'Customer Type';
}

export function getCustomerCountryLabel(): string {
  return 'Customer Country';
}

export function getAllCustomersLabel(): string {
  return 'All Customers';
}

export function getAllCustomerGroupsPlaceholder(): string {
  return 'All Customer Groups';
}

export function getAllCustomerSitesPlaceholder(): string {
  return 'All Customer Sites';
}

export function getStrategicCustomersLabel(): string {
  return 'Strategic Customers Only';
}

/** Company-type filter options (match backend company_type values). */
export function getCompanyTypeFilterLabel(companyType: 'supplier' | 'customer'): string {
  return companyType === 'supplier' ? 'Supplier' : 'Customer';
}

export type { PortalType, PortalLabels };
